import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import shadowBg from "@/assets/shadowline-1.jpg?format=webp";
import silhouette1 from "@/assets/ritual-silhouette-1.jpg?format=webp";
import silhouette2 from "@/assets/ritual-silhouette-2.jpg?format=webp";
import silhouette3 from "@/assets/ritual-silhouette-3.jpg?format=webp";
import detailImage from "@/assets/ritual-silhouette-4.jpg?format=webp";

const SlowMadeCarousel = () => {
  const { t } = useTranslation();

  const silhouettes = [
    {
      id: 1,
      name: t("carousel.solstice.name"),
      description: t("carousel.solstice.desc"),
      hoursLabel: t("carousel.solstice.hours"),
      image: silhouette1,
      material: t("carousel.solstice.material"),
    },
    {
      id: 2,
      name: t("carousel.vesper.name"),
      description: t("carousel.vesper.desc"),
      hoursLabel: t("carousel.vesper.hours"),
      image: silhouette2,
      material: t("carousel.vesper.material"),
    },
    {
      id: 3,
      name: t("carousel.meridian.name"),
      description: t("carousel.meridian.desc"),
      hoursLabel: t("carousel.meridian.hours"),
      image: silhouette3,
      material: t("carousel.meridian.material"),
    },
    {
      id: 4,
      name: t("carousel.lumina.name"),
      description: t("carousel.lumina.desc"),
      hoursLabel: t("carousel.lumina.hours"),
      image: detailImage,
      material: t("carousel.lumina.material"),
    },
  ];

  return (
    <section className="w-full py-20 md:py-32 relative overflow-hidden">
      {/* Subtle atmospheric background */}
      <div className="absolute inset-0 -z-10">
        <img src={shadowBg} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-background/92" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="px-6 mb-12"
      >
        <div className="max-w-4xl">
          <div className="gold-line mb-8" />
          <p className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4">
            {t("carousel.overline")}
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            {t("carousel.title")}
          </h2>
          <p className="font-body text-sm text-muted-foreground max-w-lg leading-relaxed">
            {t("carousel.subtitle")}
          </p>
        </div>
      </motion.div>

      {/* CSS scroll-snap carousel — no JS layout reads, no forced reflow */}
      <div
        className="px-6 flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {silhouettes.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: index * 0.15 }}
            className="snap-start shrink-0 w-[80%] sm:w-1/2 lg:w-1/3 xl:w-1/4 group cursor-pointer"
          >
            <div className="relative aspect-[3/4] mb-5 overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover transition-transform duration-1200 ease-out group-hover:scale-105"
              />
              {/* Time to Create badge */}
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2">
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-foreground">
                  {item.hoursLabel}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-gold">
                {item.material}
              </p>
              <h3 className="font-display text-2xl text-foreground">
                {item.name}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {item.description}
              </p>
              <Link
                to={`/consultation?silhouette=${encodeURIComponent(item.name)}`}
                aria-label={`${t("carousel.inquireCta")} — ${item.name}`}
                className="font-body text-xs tracking-[0.15em] uppercase text-foreground border-b border-foreground pb-0.5 hover:text-gold hover:border-gold transition-colors duration-500 mt-3 inline-block"
              >
                {t("carousel.inquireCta")}
              </Link>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default SlowMadeCarousel;
